import { createReactiveSignal } from './reactive.js';
import { hashReactiveFn, hashValue } from './utils/hash.js';
import { getCurrentConsumer } from './consumer.js';
let requestScopeGetter;
/**
 * Install or clear an optional source for a per-request (or per-environment) {@link SignalScope}
 * consulted by {@link getCurrentScope} after `CURRENT_SCOPE` and the reactive consumer's scope,
 * but before the definition-time fallback. Pass `undefined` to restore default behavior.
 *
 * React Server Components: use `setupRscRequestScope()` from `signalium/react/server` instead of
 * calling this directly.
 */
export function setRequestScopeGetter(get) {
    requestScopeGetter = get;
}
let CONTEXT_ID = 0;
export class ContextImpl {
    defaultValue;
    _key;
    _description;
    constructor(defaultValue, desc) {
        this.defaultValue = defaultValue;
        this._description = desc ?? `context:${CONTEXT_ID++}`;
        this._key = Symbol(this._description);
    }
}
export const context = (initialValue, description) => {
    return new ContextImpl(initialValue, description);
};
export function withContexts(contexts, fn) {
    const prevScope = CURRENT_SCOPE;
    const currentScope = getCurrentScope();
    try {
        CURRENT_SCOPE = currentScope.getChild(contexts);
        return fn();
    }
    finally {
        CURRENT_SCOPE = prevScope;
    }
}
export const getContext = (context) => {
    const scope = CURRENT_SCOPE ?? getCurrentConsumer()?.scope;
    if (scope === undefined) {
        throw new Error('getContext must be used within a reactive function, a withContext, or within a framework-specific context provider.');
    }
    return scope.getContext(context) ?? context.defaultValue;
};
// ======= Signal Scope =======
export class SignalScope {
    parentScope;
    constructor(contexts, parent) {
        this.parentScope = parent;
        this.contexts = Object.create(parent?.contexts || null);
        this.setContexts(contexts);
    }
    contexts;
    children = new Map();
    signals = new Map();
    setContexts(contexts) {
        for (const [context, value] of contexts) {
            this.contexts[context._key] = value;
            if (typeof value === 'object' && value !== null) {
                SCOPE_OWNER_MAP.set(value, this);
            }
        }
        this.signals.clear();
    }
    getChild(contexts) {
        const key = hashValue(contexts);
        let child = this.children.get(key);
        if (child === undefined) {
            child = new SignalScope(contexts, this);
            this.children.set(key, child);
        }
        return child;
    }
    getContext(_context) {
        const context = _context;
        return this.contexts[context._key];
    }
    get(def, args) {
        const paramKey = def.paramKey?.(...args);
        const key = hashReactiveFn(def.compute, paramKey ? [paramKey] : args);
        let signal = this.signals.get(key)?.deref();
        if (signal === undefined) {
            signal = createReactiveSignal(def, args, key, this);
            this.signals.set(key, new WeakRef(signal));
        }
        return signal;
    }
}
let GLOBAL_SCOPE = new SignalScope([]);
export const getGlobalScope = () => {
    return GLOBAL_SCOPE;
};
export function setGlobalContexts(contexts) {
    GLOBAL_SCOPE.setContexts(contexts);
}
export const clearGlobalContexts = () => {
    GLOBAL_SCOPE = new SignalScope([]);
};
let CURRENT_SCOPE;
export const setCurrentScope = (scope) => {
    CURRENT_SCOPE = scope;
};
export const getInternalCurrentScope = () => {
    return CURRENT_SCOPE;
};
export const getCurrentScope = (fallback = GLOBAL_SCOPE) => {
    return CURRENT_SCOPE ?? getCurrentConsumer()?.scope ?? requestScopeGetter?.() ?? fallback;
};
// ======= Owner =======
const SCOPE_OWNER_MAP = new WeakMap();
const OWNER_CHILD_MAP = new WeakMap();
export const setScopeOwner = (obj, ownerObject) => {
    if (OWNER_CHILD_MAP.has(obj)) {
        throw new Error('Object already has a scope owner, owners cannot be dynamic');
    }
    OWNER_CHILD_MAP.set(obj, ownerObject);
};
const reifyScopeOwner = (obj) => {
    const owner = OWNER_CHILD_MAP.get(obj);
    if (!owner) {
        throw new Error('Object has no scope owner, reactiveMethod must be attached to an owned context object');
    }
    const scope = SCOPE_OWNER_MAP.get(owner) ?? reifyScopeOwner(owner);
    SCOPE_OWNER_MAP.set(obj, scope);
    return scope;
};
export const getScopeOwner = (obj) => {
    let scope = SCOPE_OWNER_MAP.get(obj);
    if (!scope) {
        scope = reifyScopeOwner(obj);
    }
    return scope;
};
// ======= Test Helper =======
export function getSignalsMap(scope) {
    return scope.signals;
}
